import { ActivityLogRecord } from '@this+that/models';

import {
  ActivityLogsEntryState,
  HandlerSet,
  InboxEntryActivityLogsState,
} from './types';

export const INITIAL_ENTRY_STATE: ActivityLogsEntryState = {
  activityLogs: [],
  exclusiveStartKey: null,
  hasMore: false,
  isLoading: false,
  isLoadingMore: false,
  error: null,
};

const updateEntry = (
  set: HandlerSet,
  inboxEntryId: string,
  updater: (entry: ActivityLogsEntryState) => Partial<ActivityLogsEntryState>,
) => {
  set((state: InboxEntryActivityLogsState) => {
    const current = state.stateByEntry[inboxEntryId] ?? {
      ...INITIAL_ENTRY_STATE,
    };
    return {
      stateByEntry: {
        ...state.stateByEntry,
        [inboxEntryId]: {
          ...current,
          ...updater(current),
        },
      },
    };
  });
};

export const handleInitEntry = (set: HandlerSet, inboxEntryId: string) => {
  set((state: InboxEntryActivityLogsState) => {
    if (state.stateByEntry[inboxEntryId]) {
      return state;
    }
    return {
      stateByEntry: {
        ...state.stateByEntry,
        [inboxEntryId]: { ...INITIAL_ENTRY_STATE },
      },
    };
  });
};

export const handleClearEntry = (set: HandlerSet, inboxEntryId: string) => {
  set((state: InboxEntryActivityLogsState) => {
    const { [inboxEntryId]: _, ...rest } = state.stateByEntry;
    return { stateByEntry: rest };
  });
};

export const handleSetActivityLogs = (
  set: HandlerSet,
  inboxEntryId: string,
  logs: ActivityLogRecord[],
  exclusiveStartKey: string | null,
) => {
  updateEntry(set, inboxEntryId, () => ({
    activityLogs: logs,
    exclusiveStartKey,
    hasMore: exclusiveStartKey !== null,
    isLoading: false,
    error: null,
  }));
};

export const handleAppendActivityLogs = (
  set: HandlerSet,
  inboxEntryId: string,
  logs: ActivityLogRecord[],
  exclusiveStartKey: string | null,
) => {
  updateEntry(set, inboxEntryId, (current) => {
    const existingIds = new Set(current.activityLogs.map((l) => l.timestampId));
    const newLogs = logs.filter((l) => !existingIds.has(l.timestampId));
    return {
      activityLogs: [...current.activityLogs, ...newLogs],
      exclusiveStartKey,
      hasMore: exclusiveStartKey !== null,
      isLoadingMore: false,
    };
  });
};

export const handlePrependActivityLog = (
  set: HandlerSet,
  inboxEntryId: string,
  log: ActivityLogRecord,
) => {
  updateEntry(set, inboxEntryId, (current) => {
    if (current.activityLogs.some((l) => l.timestampId === log.timestampId)) {
      return current;
    }
    return { activityLogs: [log, ...current.activityLogs] };
  });
};

export const handleSetLoading = (
  set: HandlerSet,
  inboxEntryId: string,
  isLoading: boolean,
) => {
  updateEntry(set, inboxEntryId, () => ({ isLoading }));
};

export const handleSetLoadingMore = (
  set: HandlerSet,
  inboxEntryId: string,
  isLoadingMore: boolean,
) => {
  updateEntry(set, inboxEntryId, () => ({ isLoadingMore }));
};

export const handleSetError = (
  set: HandlerSet,
  inboxEntryId: string,
  error: string | null,
) => {
  updateEntry(set, inboxEntryId, () => ({ error }));
};
